import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from "@reduxjs/toolkit";
import { AutoShipment, BackorderedKit, RefItem, UpcomingShipment } from "./types";
import * as shipmentService from './shipmentService';
import { RootState } from "../../../redux/store";
import { MedicationsMetadata } from '../../../types'

const SLICE_PREFIX = 'shipments';
export type LoadingState = 'not-asked' | 'loading' | 'done' | 'error';

interface State {
  upcomingShipments: Record<number, UpcomingShipment[]> | null;
  shipmentCustomerNumber: string | null;
  medicationsMetadata: MedicationsMetadata | null;
  backorderedKits: { [Identifier: string]: BackorderedKit } | null;
  autoShipments: AutoShipment[] | null;
  refItems: RefItem[] | null;
  loadingState: LoadingState | null;
}

const initialState: State = {
  upcomingShipments: null,
  shipmentCustomerNumber: null,
  medicationsMetadata: null,
  backorderedKits: null,
  autoShipments: null,
  refItems: null,
  loadingState: 'not-asked'
}

export const getUpcomingShipments = createAsyncThunk(
  `${SLICE_PREFIX}/getUpcomingShipments`,
  async (customerNumber: string): Promise<Record<number, UpcomingShipment[]>> => {
    const data = await shipmentService.getUpcomingShipments(customerNumber);
    return data;
  }
)

export const getMedicationMetadata = createAsyncThunk(
  `${SLICE_PREFIX}/getMedicationMetadata`,
  async (customerNumber: string): Promise<MedicationsMetadata> => {
    const data = await shipmentService.getMedicationMetadata(customerNumber);
    return data;
  }
)

export const getAutoShipments = createAsyncThunk(
  `${SLICE_PREFIX}/getAutoShipments`,
  async (customerNumber: string): Promise<AutoShipment[]> => {
    const data = await shipmentService.getAutoShipments(customerNumber);
    return data;
  }
)

export const getRefItems = createAsyncThunk(
  `${SLICE_PREFIX}/getRefItems`,
  async (values: { customerNumber: string, autoShipments: AutoShipment[] }) => {
    const {customerNumber, autoShipments} = values
    const refItems = await shipmentService.getRefItems(customerNumber, autoShipments)

    return refItems
  }
)

export const getBackorderedKits = createAsyncThunk(
  `${SLICE_PREFIX}/getBackorderedKits`,
  async (customerNumber: string): Promise<{ [Identifier: string]: BackorderedKit }> => {
    const data = await shipmentService.getBackorderedKits(customerNumber);
    return data;
  }
)

export const getUpcomingShipmentData = createAsyncThunk(
  `${SLICE_PREFIX}/getUpcomingShipmentData`,
  async (customerNumber: string, { dispatch }) => {
    await Promise.all([
      dispatch(getUpcomingShipments(customerNumber)),
      dispatch(getMedicationMetadata(customerNumber)),
      dispatch(getAutoShipments(customerNumber))
    ]);

    return { customerNumber };
  }
);

export const shipmentsSlice = createSlice({
  name: SLICE_PREFIX,
  initialState,
  reducers: {
    clearShipments: state => {
      state.shipmentCustomerNumber = initialState.shipmentCustomerNumber
      state.autoShipments = initialState.autoShipments
      state.upcomingShipments = initialState.upcomingShipments
      state.medicationsMetadata = initialState.medicationsMetadata
      state.loadingState = initialState.loadingState
    },
    clearRefItems: state => {
      state.refItems = initialState.refItems
    },
    setShipmentCustomerNumber: (state, action: PayloadAction<string | null>) => {
      state.shipmentCustomerNumber = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getUpcomingShipments.fulfilled, (state, { payload }) => {
      state.upcomingShipments = payload;
    })

    builder.addCase(getMedicationMetadata.fulfilled, (state, { payload }) => {
      state.medicationsMetadata = payload;
    })

    builder.addCase(getAutoShipments.fulfilled, (state, { payload }) => {
      state.autoShipments = payload;
    })

    builder.addCase(getBackorderedKits.fulfilled, (state, { payload }) => {
      state.backorderedKits = payload;
    })

    builder.addCase(getRefItems.fulfilled, (state, { payload }) => {
      state.refItems = payload
    })

    builder.addMatcher(isPending(getUpcomingShipmentData), (state) => {
      state.loadingState = 'loading';
    });

    builder.addMatcher(isRejected(getUpcomingShipmentData), (state) => {
      state.loadingState = 'error';
    });

    builder.addMatcher(isFulfilled(getUpcomingShipmentData), (state, { payload }) => {
      state.loadingState = 'done';
    });
  }
})

export const { clearShipments, clearRefItems, setShipmentCustomerNumber } = shipmentsSlice.actions;

export const selectUpcomingShipments = (state: RootState) => state.shipments.upcomingShipments;
export const selectShipmentCustomerNumber = (state: RootState) => state.shipments.shipmentCustomerNumber;
export const selectMedicationsMetadata = (state: RootState) => state.shipments.medicationsMetadata;
export const selectAutoShipments = (state: RootState) => state.shipments.autoShipments;
export const selectLoadingState = (state: RootState) => state.shipments.loadingState; 
export const selectBackorderedKits = (state: RootState) => state.shipments.backorderedKits;
export const selectRefItems = (state: RootState) => state.shipments.refItems;

export const selectShipmentsData = (state: RootState) => {
  return {
    shipmentCustomerNumber: selectShipmentCustomerNumber(state),
    autoShipments: selectAutoShipments(state),
    upcomingShipments: selectUpcomingShipments(state),
    medicationsMetadata: selectMedicationsMetadata(state),
    loadingState: selectLoadingState(state),
  }
}

export default shipmentsSlice.reducer;